import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetLotteriesForEventQuery, GetLotteryQuery as GetLottery } from '__generated__/graphql';

import { encodeUrl, getServerErrors } from '../../../common/helpers';
import DateRangeForm, { dateRangeObject } from '../../Common/DateRangeForm';
import UI from '../../../common/UI';
import useEditLotteryMutation from './useEditLotteryMutation';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useProject from '../../useProject';
import useValidation from '../../../common/useValidation';

type Lottery = GetLottery['project']['lottery'];

const getEditLotteryConfirmationSchema = (required: boolean) => yup.object().shape(
  required ? dateRangeObject(true, true) : {},
);

export interface EditLotteryConfirmationFormProps {
  lottery: Lottery;
  event: GetLotteriesForEventQuery['project']['event'];
}

const EditLotteryConfirmationForm = ({ lottery, event }: EditLotteryConfirmationFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const getValues = (lottery: Lottery) => ({
    enable_confirmation: !!lottery.start_date && !!lottery.end_date,
    start_date: lottery.start_date,
    end_date: lottery.end_date,
  });

  const form = useForm({
    values: getValues(lottery),
  });

  useNavigationPrompt(form.changed());

  // Re-initialize DateRangeForm after saving.
  const [formKey, setFormKey] = useState(0);

  const [editLottery, { error, loading }] = useEditLotteryMutation({
    input: {
      id: lottery.id,
      confirmation: {
        start_date: form.values.enable_confirmation ? form.values.start_date : null,
        end_date: form.values.enable_confirmation ? form.values.end_date : null,
      },
    },
    onSuccess: (lottery) => {
      form.reset(getValues(lottery));
      setFormKey((key) => key + 1);
    },
  });

  const { valid, errors } = useValidation({
    schema: getEditLotteryConfirmationSchema(form.values.enable_confirmation),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editLottery()} key={formKey}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('confirmation_period')}
          </UI.Legend>
          <UI.InputDescription>
            {t('lottery_confirmation_period_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup>
          <UI.InputLabel htmlFor="LotteryEnableConfirmation">
            {t('allow_confirmation')}
          </UI.InputLabel>
          <UI.Checkbox {...form.checkbox('enable_confirmation')} id="LotteryEnableConfirmation">
            {t('set_confirmation_period')}
          </UI.Checkbox>
        </UI.InputGroup>

        {form.values.enable_confirmation && (
          <DateRangeForm form={form} errors={errors} requireStart requireEnd />
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_lottery')}
          closeTo={`/${project.id}/participants/lotteries?${encodeUrl({ event: event.id })}`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditLotteryConfirmationForm;
